import OpenPlayGroupBooking from '../components/OpenPlayGroupBooking';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Trophy, Users, Calendar, Clock, MapPin, UserX, UserCheck, Pencil, Info, Swords, Plus, Copy, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { sessionBasePath } from '../utils/sessionNavigation';
import { formatDate } from '../utils/formatters';
import ConfirmModal from '../components/ConfirmModal';
import OpenPlayMatches from '../components/OpenPlayMatches';

export default function OpenPlayDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const delegated = user?.role !== 'CourtOwner' && user?.hasOpenPlayOrganiserAccess;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showGroupBooking, setShowGroupBooking] = useState(false);
  const [addPlayer, setAddPlayer] = useState({ playerEmail: '', playerName: '', paymentMethod: 'Cash', rentEquipment: false });
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [newPaymentLink, setNewPaymentLink] = useState(null);
  const [playerSuggestions, setPlayerSuggestions] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [refreshingPlayers, setRefreshingPlayers] = useState(false);
  const [playersUpdatedAt, setPlayersUpdatedAt] = useState(null);

  const detailId = detail?.id;
  const detailActivityType = detail?.activityType;
  useEffect(() => { loadDetail(); }, [id]);
  useEffect(() => {
    if (!detailId || String(detailId) !== id) return;
    const canonical = `${sessionBasePath(detailActivityType)}/${id}`;
    if (location.pathname !== canonical) navigate(`${canonical}${location.search}`, { replace: true });
  }, [detailId, detailActivityType, id, location.pathname, location.search, navigate]);
  useEffect(() => {
    if (searchParams.get('addPlayer') !== '1') return;
    setActiveTab('players');
    setShowAddPlayer(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (activeTab !== 'players') return undefined;
    let active = true;
    let inFlight = false;
    const refresh = async () => {
      if (!active || inFlight || document.visibilityState !== 'visible') return;
      inFlight = true;
      setRefreshingPlayers(true);
      try {
        const { data } = await api.get(`/openplays/${id}`);
        if (active) {
          setDetail(data);
          setPlayersUpdatedAt(new Date());
        }
      } catch {
        // Keep the current roster visible; the next poll or manual refresh can retry.
      } finally {
        inFlight = false;
        if (active) setRefreshingPlayers(false);
      }
    };
    refresh();
    const interval = window.setInterval(refresh, 15000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [activeTab, id]);

  async function refreshPlayersNow() {
    if (refreshingPlayers) return;
    setRefreshingPlayers(true);
    try {
      const { data } = await api.get(`/openplays/${id}`);
      setDetail(data);
      setPlayersUpdatedAt(new Date());
    } catch { toast.error('Unable to refresh players right now'); }
    finally { setRefreshingPlayers(false); }
  }

  useEffect(() => {
    const term = addPlayer.playerEmail.trim();
    if (!showAddPlayer || selectedPlayer || term.length < 2) {
      setPlayerSuggestions([]);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await api.get('/users/search', { params: { q: term } });
        if (active) setPlayerSuggestions(data.users || []);
      } catch { if (active) setPlayerSuggestions([]); }
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [addPlayer.playerEmail, selectedPlayer, showAddPlayer]);

  async function loadDetail() {
    try {
      const res = await api.get(`/openplays/${id}`);
      setDetail(res.data);
    } catch { toast.error('Failed to load details'); }
    finally { setLoading(false); }
  }

  async function handleRemovePlayer() {
    if (!confirmTarget) return;
    const { registrationId } = confirmTarget;
    setRemoving(registrationId);
    try {
      await api.delete(`/openplays/${id}/players/${registrationId}`);
      await loadDetail();
      toast.success('Player removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove player');
    }
    setRemoving(null);
    setConfirmTarget(null);
  }

  async function updateRoster(player, changes) {
    try {
      await api.put(`/openplays/${id}/players/${player.id}/roster`, changes);
      setDetail((prev) => ({
        ...prev,
        players: prev.players.map((item) => item.id === player.id ? {
          ...item,
          ...(changes.checkedIn === undefined ? {} : { checkedInAt: changes.checkedIn ? new Date().toISOString() : null }),
          ...(changes.clearCourt ? { courtId: null, courtName: null } : {}),
          ...(changes.courtId ? { courtId: changes.courtId, courtName: prev.courts?.find((court) => court.courtId === changes.courtId)?.name } : {}),
        } : item),
      }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to update the roster');
    }
  }

  async function reviewJoinRequest(request, approve) {
    setReviewingRequest(request.id);
    try {
      const response = await api.post(`/openplays/${id}/players/${request.id}/${approve ? 'approve' : 'decline'}`);
      await loadDetail();
      toast.success(response.data?.message || (approve ? 'Player approved' : 'Join request declined'));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to review the join request');
    } finally {
      setReviewingRequest(null);
    }
  }

  async function handleAddPlayer(event) {
    event.preventDefault();
    if (!addPlayer.playerEmail.includes('@')) {
      toast.error('Select an existing FREAC player or enter a complete email address.');
      return;
    }
    setAddingPlayer(true);
    try {
      const { data } = await api.post(delegated ? `/openplays/${id}/invites` : `/payments/openplays/${id}/owner-player`, addPlayer);
      await loadDetail();
      setShowAddPlayer(false);
      setAddPlayer({ playerEmail: '', playerName: '', paymentMethod: 'Cash', rentEquipment: false });
      setSelectedPlayer(null);
      setPlayerSuggestions([]);
      setNewPaymentLink(data.paymentLink ? { url: data.paymentLink, name: data.playerName, expiresAt: data.paymentExpiresAt } : null);
      toast.success(delegated ? 'Player invited to join from their FREAC account.' : data.paymentLink ? 'Payment link email queued; you can also copy it' : 'Player confirmed; confirmation email queued');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to add player');
    } finally { setAddingPlayer(false); }
  }

  async function copyPaymentLink(url) {
    try { await navigator.clipboard.writeText(url); toast.success('Payment link copied'); }
    catch { toast.error('Could not copy the link. Please copy it manually.'); }
  }

  async function copyPendingPaymentLink(registrationId) {
    try {
      const { data } = await api.get(`/payments/openplays/${registrationId}/owner-payment-link`);
      await copyPaymentLink(data.paymentLink);
    } catch (error) { toast.error(error.response?.data?.message || 'Payment link is no longer active'); }
  }

  function formatTime(t) {
    if (!t) return '';
    const [h, m] = (typeof t === 'string' ? t : '').split(':').map(Number);
    if (isNaN(h)) return t;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : (h > 12 ? h - 12 : h);
    return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
  }

  function paymentState(player) {
    if (player.paymentStarted) return { label: 'Payment started · checking', color: 'bg-[#E8F4FD] text-[#264D59]' };
    if (player.paymentExpiresAt && new Date(player.paymentExpiresAt) <= new Date())
      return { label: 'Payment link expired', color: 'bg-[#FDF1EB] text-[#B3593E]' };
    return { label: 'Link sent · awaiting payment', color: 'bg-[#FFF0BE] text-[#795600]' };
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="text-center py-16">
        <Trophy className="mx-auto h-12 w-12 text-[#94A3B8]" />
        <p className="mt-4 text-[#6B7280]">Session not found</p>
      </div>
    );
  }

  const isStepDance = detail.activityType === 'StepDance';
  const participant = isStepDance ? 'dancer' : 'player';
  const participants = isStepDance ? 'Dancers' : 'Players';
  const basePath = sessionBasePath(detail.activityType);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(basePath)} className="btn-outline"><ArrowLeft className="h-4 w-4 mr-1" /> Back</button>
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">{detail.name}</h1>
          <p className="text-sm text-[#6B7280]">{detail.activityType === 'StepDance' ? 'Step Dance' : 'Open Play'} Details</p>
          {detail.activityType !== 'StepDance' && Number(detail.pricePerPlayer) === 0 && <p className="text-sm text-[#6B7280]">{detail.blocksCourtBookings === false ? 'Courts remain available for other bookings; court time is not reserved.' : 'Courts blocked for this session.'}</p>}
          {detail.activityType === 'StepDance' && <p className="text-sm text-[#6B7280]">Instructor: {detail.instructorName || 'To be confirmed'} · Equipment: {detail.equipmentNotes || 'Bring your own'}{detail.equipmentRentalFee > 0 ? ` · Rental PHP ${Number(detail.equipmentRentalFee).toFixed(2)} per dancer` : ''}</p>}
        </div>
        {(detail.isOrganiser || detail.canManageJoinRequests) && <button type="button" onClick={() => navigate(`${basePath}/new?copyFrom=${id}`)} className="btn-outline"><Copy className="h-4 w-4 mr-1" /> Copy session</button>}
        {detail.status === 'Active' && <div className="ml-auto flex flex-wrap gap-2">
          {(Number(detail.pricePerPlayer) > 0 || Number(detail.equipmentRentalFee) > 0) && <button className="btn-outline" onClick={() => setShowGroupBooking(true)}>Group payment link</button>}
          {detail.activityType === 'StepDance' && !delegated && (Number(detail.pricePerPlayer) > 0 || Number(detail.equipmentRentalFee) > 0) && <button type="button" className="btn-outline" onClick={() => { setActiveTab('players'); setShowAddPlayer(true); setAddPlayer(value => ({ ...value, paymentMethod: 'QRPh' })); }}><Copy className="h-4 w-4 mr-1" /> Create payment link</button>}
          <button type="button" onClick={() => { setActiveTab('players'); setShowAddPlayer(true); }} className="btn-primary"><Plus className="h-4 w-4 mr-1" /> {detail.activityType === 'StepDance' ? (delegated ? 'Invite dancer' : 'Add dancer') : (delegated ? 'Invite player' : 'Add player')}</button>
          <button onClick={() => navigate(`${basePath}/${id}/edit`)} className="btn-outline"><Pencil className="h-4 w-4 mr-1" /> Edit</button>
        </div>}
      </div>

      <div className="card !p-2">
        <div className={`grid ${isStepDance ? 'grid-cols-2' : 'grid-cols-3'} gap-2`}>
          {[
            { id: 'overview', label: 'Overview', icon: Info },
            { id: 'players', label: `${detail.activityType === 'StepDance' ? 'Dancers' : 'Players'} (${detail.playerCount})`, icon: Users },
            { id: 'matches', label: 'Matches', icon: Swords },
          ].filter(tab => detail.activityType !== 'StepDance' || tab.id !== 'matches').map((tab) => (
            <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`flex items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${activeTab === tab.id ? 'bg-[#264D59] text-white shadow-sm' : 'text-[#667085] hover:bg-[#F1F5F9] hover:text-[#1F2937]'}`}>
              <tab.icon className="mr-2 h-4 w-4" />{tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'overview' && <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-2 text-[#6B7280] mb-1">
            <MapPin className="h-4 w-4" />
            <span className="text-xs uppercase">Venue</span>
          </div>
          <p className="text-[#1F2937] font-medium">{detail.venueName}</p>
          <p className="text-xs text-[#6B7280] mt-1">{detail.city}</p>
          {isStepDance && <p className="mt-2 text-sm">Dance space: {detail.blocksCourtBookings === false ? 'Dedicated dance space' : (detail.courts || []).map(c => c.name).join(', ') || 'Reserved venue space'}</p>}
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-2 text-[#6B7280] mb-1">
            <Calendar className="h-4 w-4" />
            <span className="text-xs uppercase">Date</span>
          </div>
          <p className="text-[#1F2937] font-medium">{formatDate(detail.date)}</p>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-2 text-[#6B7280] mb-1">
            <Clock className="h-4 w-4" />
            <span className="text-xs uppercase">Time</span>
          </div>
          <p className="text-[#1F2937] font-medium">{formatTime(detail.startTime)} — {formatTime(detail.endTime)}{detail.endTime < detail.startTime ? ' (next day)' : ''}</p>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-2 text-[#6B7280] mb-1">
            <Users className="h-4 w-4" />
            <span className="text-xs uppercase">{participants}</span>
          </div>
          <p className="text-[#1F2937] font-medium">
            {detail.playerCount} / {detail.maxPlayers}
          </p>
        </div>
        </div>

        {(detail.description || detail.pricePerPlayer > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {detail.description && (
            <div className="card p-4">
              <p className="text-xs text-[#6B7280] uppercase mb-2">Description</p>
              <p className="text-sm text-[#1F2937]">{detail.description}</p>
            </div>
          )}
          {detail.pricePerPlayer > 0 && (
            <div className="card p-4">
              <p className="text-xs text-[#6B7280] uppercase mb-2">Price Per {isStepDance ? 'Dancer' : 'Player'}</p>
              <p className="text-2xl font-bold text-[#264D59]">PHP {detail.pricePerPlayer.toFixed(0)}</p>
            </div>
          )}
        </div>
        )}
      </>}

      {activeTab === 'players' && <>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[#64748B]">
          <span>{participants} and payment status update automatically every 15 seconds while this tab is open.{playersUpdatedAt && ` Last checked ${playersUpdatedAt.toLocaleTimeString()}.`}</span>
          <button type="button" disabled={refreshingPlayers} onClick={refreshPlayersNow} className="btn-outline text-xs px-3 py-1.5 disabled:opacity-50"><RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshingPlayers ? 'animate-spin' : ''}`} />Refresh now</button>
        </div>
        {detail.status === 'Active' && (
          <div className="card p-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><h3 className="font-semibold text-[#1F2937]">Add a {participant}</h3><p className="text-sm text-[#64748B]">Use an existing FREAC email or add a guest. Cash confirms immediately; GCash and QR Ph create a link for the {participant}.</p></div>
              <button type="button" className="btn-primary" onClick={() => setShowAddPlayer((value) => !value)}><Plus className="h-4 w-4 mr-1" /> {detail.activityType === 'StepDance' ? (delegated ? 'Invite dancer' : 'Add dancer') : (delegated ? 'Invite player' : 'Add player')}</button>
            </div>
            {showAddPlayer && <form onSubmit={handleAddPlayer} className="grid gap-3 md:grid-cols-2 border-t pt-4">
              <div className="relative"><label className="text-sm font-medium">Find {participant} or enter email<input required type="text" autoComplete="off" placeholder="Search name or email" className="input-field mt-1" value={addPlayer.playerEmail} onChange={(event) => { setSelectedPlayer(null); setAddPlayer((value) => ({ ...value, playerEmail: event.target.value })); }} /></label>{selectedPlayer && <p className="mt-1 text-xs text-[#07826F]">Existing FREAC {participant}: {selectedPlayer.firstName} {selectedPlayer.lastName}</p>}{playerSuggestions.length > 0 && <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-xl border bg-white shadow-lg">{playerSuggestions.map((player) => <button key={player.id} type="button" className="block w-full border-b px-3 py-2 text-left text-sm hover:bg-[#EAF4F5]" onClick={() => { setSelectedPlayer(player); setPlayerSuggestions([]); setAddPlayer((value) => ({ ...value, playerEmail: player.email, playerName: `${player.firstName} ${player.lastName}`.trim() })); }}><span className="block font-medium">{player.firstName} {player.lastName}</span><span className="text-xs text-[#64748B]">{player.email}</span></button>)}</div>}</div>
              <label className="text-sm font-medium">{isStepDance ? 'Dancer' : 'Player'} name <span className="font-normal text-[#64748B]">(required for guests)</span><input className="input-field mt-1" value={addPlayer.playerName} onChange={(event) => setAddPlayer((value) => ({ ...value, playerName: event.target.value }))} /></label>
              {!delegated && detail.activityType === 'StepDance' && <label className="text-sm font-medium">Equipment<select className="input-field mt-1" value={addPlayer.rentEquipment ? 'rent' : 'own'} onChange={event => setAddPlayer(value => ({ ...value, rentEquipment: event.target.value === 'rent' }))}><option value="own">Bring own equipment — no extra fee</option>{detail.equipmentRentalFee > 0 && <option value="rent">Rent at venue — PHP {Number(detail.equipmentRentalFee).toFixed(2)} extra</option>}</select><span className="mt-1 block text-xs text-[#64748B]">Session + equipment: PHP {(Number(detail.pricePerPlayer) + (addPlayer.rentEquipment ? Number(detail.equipmentRentalFee) : 0)).toFixed(2)}</span></label>}
              {!delegated && <label className="text-sm font-medium">Payment method<select className="input-field mt-1" value={addPlayer.paymentMethod} onChange={(event) => setAddPlayer((value) => ({ ...value, paymentMethod: event.target.value }))}><option value="Cash">Cash — already received</option><option value="GCash">GCash — send payment link</option><option value="QRPh">QR Ph — send payment link</option></select></label>}
              <div className="flex items-end"><button disabled={addingPlayer} className="btn-primary">{addingPlayer ? 'Saving…' : delegated ? 'Send invitation' : addPlayer.paymentMethod === 'Cash' ? `Confirm ${participant} and cash received` : 'Reserve place and create link'}</button></div>
              {!delegated && addPlayer.paymentMethod !== 'Cash' && <p className="md:col-span-2 text-xs text-[#64748B]">We will email the payment link to the {participant}. You can also copy it below. The {participant} has 30 minutes to start payment; their place is confirmed after PayMongo verifies it.</p>}
            </form>}
            {newPaymentLink && <div className="rounded-xl bg-[#E8F4FD] p-4"><p className="text-sm font-semibold">Share this payment link with {newPaymentLink.name}</p><p className="text-xs text-[#64748B]">Expires {new Date(newPaymentLink.expiresAt).toLocaleString()}</p><div className="mt-2 flex gap-2"><input readOnly className="input-field flex-1" value={newPaymentLink.url} onFocus={(event) => event.target.select()} /><button type="button" className="btn-outline" onClick={() => copyPaymentLink(newPaymentLink.url)}><Copy className="h-4 w-4 mr-1" /> Copy</button></div></div>}
          </div>
        )}
        {detail.pendingPayments?.length > 0 && <div className="card p-5"><h3 className="font-semibold mb-3">Awaiting payment ({detail.pendingPayments.length})</h3><div className="space-y-2">{detail.pendingPayments.map((player) => <div key={player.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#FFF8E7] p-3"><div><p className="text-sm font-medium">{player.name}{detail.activityType === 'StepDance' ? (player.rentEquipment ? ' · Rent at venue' : ' · Own equipment') : ''} · {player.paymentMethod} · PHP {Number(player.amount).toFixed(2)}</p><p className="text-xs text-[#64748B]">{player.email} · Due {player.paymentExpiresAt ? new Date(player.paymentExpiresAt).toLocaleString() : 'soon'}</p><span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${paymentState(player).color}`}>{paymentState(player).label}</span></div><div className="flex gap-2"><button type="button" disabled={player.paymentExpiresAt && new Date(player.paymentExpiresAt) <= new Date()} className="btn-outline text-xs" onClick={() => copyPendingPaymentLink(player.id)}>Copy payment link</button><button type="button" disabled={player.paymentStarted || removing === player.id} className="btn-outline text-xs text-[#D46C4E] border-[#D46C4E]/30 disabled:opacity-50" title={player.paymentStarted ? 'Payment has started; check its status before removal' : `Remove pending ${participant} and invalidate payment link`} onClick={() => setConfirmTarget({ registrationId: player.id, name: player.name, pending: true })}>Remove</button></div></div>)}</div></div>}
        {detail.pendingJoinRequests?.length > 0 && (
        <div className="mb-4 rounded-2xl border border-[#E7D89B] bg-[#FFFDF4] p-5">
          <div className="flex items-center gap-2"><UserCheck className="h-5 w-5 text-[#8A6500]" /><div><h3 className="font-semibold text-[#1F2937]">Join requests ({detail.pendingJoinRequests.length})</h3><p className="text-xs text-[#6B7280]">Approve a {participant} before they join or complete payment.</p></div></div>
          <div className="mt-3 space-y-2">
            {detail.pendingJoinRequests.map((request) => (
              <div key={request.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#EEE5C4] bg-white p-3">
                <div><p className="text-sm font-medium text-[#1F2937]">{request.name || 'Player'}</p>{request.guestEmail && <p className="text-xs text-[#6B7280]">{request.guestEmail}</p>}</div>
                <div className="flex gap-2">
                  <button disabled={reviewingRequest === request.id} onClick={() => reviewJoinRequest(request, false)} className="btn-outline text-xs text-[#D46C4E] border-[#D46C4E]/30">Decline</button>
                  <button disabled={reviewingRequest === request.id} onClick={() => reviewJoinRequest(request, true)} className="btn-primary text-xs">{reviewingRequest === request.id ? 'Saving…' : 'Approve'}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}
        <div className="card p-6">
        <h3 className="text-sm font-semibold text-[#1F2937] uppercase mb-4">
          {detail.activityType === 'StepDance' ? 'Dancers' : 'Players'} ({detail.playerCount})
        </h3>
        {detail.players?.length > 0 ? (
          <div className="space-y-2">
            {detail.players.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 p-3 bg-[#F1F5F9] rounded-[12px]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#E8F4FD] flex items-center justify-center text-[#264D59] text-sm font-bold">
                    {(p.name || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <span className="text-sm text-[#1F2937]">{p.name}</span>{detail.activityType === 'StepDance' && <p className="text-xs text-[#64748B]">{p.rentEquipment ? `Rent at venue · PHP ${Number(p.equipmentRentalAmount).toFixed(2)}` : 'Bringing own equipment'}</p>}
                    {p.courtName && <p className="text-xs text-[#6B7280] mt-0.5">Assigned: {p.courtName}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select value={p.courtId || ''} onChange={(event) => updateRoster(p, event.target.value ? { courtId: event.target.value } : { clearCourt: true })} className="text-xs border border-[#CBD5E1] rounded-lg px-2 py-1.5 bg-white">
                    <option value="">{isStepDance ? 'No assigned space' : 'No court'}</option>
                    {(detail.courts || []).map((court) => <option key={court.courtId} value={court.courtId}>{court.name}</option>)}
                  </select>
                  <button onClick={() => updateRoster(p, { checkedIn: !p.checkedInAt })} className={`px-2 py-1.5 rounded-lg text-xs font-medium ${p.checkedInAt ? 'bg-[#DFF4E7] text-[#237A45]' : 'bg-white border border-[#CBD5E1] text-[#475569]'}`} title={p.checkedInAt ? 'Undo check-in' : `Check in ${participant}`}>
                    <UserCheck className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setConfirmTarget({ registrationId: p.id, name: p.name })}
                    disabled={removing === p.id}
                    className="p-1.5 text-[#D46C4E] hover:text-[#B3593E] hover:bg-[#FDF1EB] rounded-lg transition-colors disabled:opacity-50"
                    title={`Remove ${participant}`}
                  >
                    {removing === p.id ? <div className="w-4 h-4 border-2 border-[#D46C4E] border-t-transparent rounded-full animate-spin" /> : <UserX className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Users className="mx-auto h-8 w-8 text-[#94A3B8]" />
            <p className="mt-2 text-sm text-[#6B7280]">No {participant}s yet</p>
          </div>
        )}
        </div>

        {detail.waitlist?.length > 0 && (
        <div className="card p-6">
          <h3 className="text-sm font-semibold text-[#1F2937] uppercase mb-4">Waitlist ({detail.waitlist.length})</h3>
          <div className="space-y-2">
            {detail.waitlist.map((player, index) => (
              <div key={player.id} className="flex items-center gap-3 p-3 bg-[#FFF8E7] rounded-[12px]">
                <span className="w-7 h-7 rounded-full bg-[#F7D36A] text-[#5B4700] text-xs font-bold flex items-center justify-center">{index + 1}</span>
                <div><p className="text-sm font-medium text-[#1F2937]">{player.name}</p>{player.guestEmail && <p className="text-xs text-[#6B7280]">{player.guestEmail}</p>}</div>
              </div>
            ))}
          </div>
        </div>
        )}
      </>}

      {detail.activityType !== 'StepDance' && activeTab === 'matches' && (
        <OpenPlayMatches
          openPlayId={id}
          players={detail.players || []}
          courts={detail.courts || []}
          isActive={detail.status === 'Active'}
        />
      )}

      {showGroupBooking && <OpenPlayGroupBooking session={detail} api={api} createLink onClose={() => setShowGroupBooking(false)} onReady={async (data) => {
        setShowGroupBooking(false); await loadDetail();
        setNewPaymentLink({ url: data.paymentLink, name: 'the group booking contact', expiresAt: data.paymentExpiresAt });
        setActiveTab('players');
      }} />}
      <ConfirmModal
        open={!!confirmTarget}
        title={`Remove ${isStepDance ? 'Dancer' : 'Player'}`}
        message={confirmTarget?.pending ? `Remove "${confirmTarget.name}" and invalidate their unpaid payment link? They will need a new invitation to pay.` : `Are you sure you want to remove "${confirmTarget?.name}" from this session?`}
        confirmLabel={removing ? 'Removing...' : 'Remove'}
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleRemovePlayer}
        onCancel={() => { if (!removing) setConfirmTarget(null); }}
      />
    </div>
  );
}
